/* eslint-disable prettier/prettier */
import { ContentType } from '../ContentType';
import { WorkbookCard } from '../../components/CardView/ContentCard/WorkbookCard';
import { Messages } from '../../../../gen-src/Localize';
import { AppUrls } from '../../../app/AppUrls';
import { ConnectedOwnerFilter } from '../../components/filters/quickFilter/OwnerFilter';
import { IContentDefinition } from './IContentDefinition';
import { ConnectedModifiedTimeFilter } from '../../components/filters/quickFilter/ModifiedTimeFilter';
import { SearchResponseHitsContent } from '../../../generated/content-exploration';
import GetMetaDataRequests from '../../services/httpClient/GetMetaDataRequests';
import { Workbook } from '../SearchResultTypes';
import { LocationTypes } from '../ILocationInfo';
import { GetWorkbooksResult } from '../httpClient/MetaDataResults';
import { SessionManagementService } from '../../services/SessionManagementService';
import { UserMapper } from '../../../common/utils/UserMapper';
import { IUser } from '../IUser';
import { SortBy } from '../SortSelection';
import { CommonColumns, IColumnFactoryParameters } from '../../components/listView/ListViewGridColumnGenerator';
import { defaultHeaderFormatter } from '../../components/listView/DefaultHeaderFormatter';
import { LocalizedCountCell, SizeCell } from '../../components/listView/dataGridCells/PlainTextAndNumberCells';
import { DateTimeCell } from '../../components/listView/dataGridCells/DateTimeCells';
import { ConnectedTagFilter } from '../../components/filters/quickFilter/TagFilter';

export const workbookContentDefinition: IContentDefinition<Workbook, GetWorkbooksResult> = {
  getSectionTitle: () => Messages.WorkbookSectionTitle(),
  getUrl: (urls) => urls.searchWorkbooksUrl(),
  getPath: (urls) => urls.searchWorkbooksPath(),
  mapCRASSData: (crassData: SearchResponseHitsContent[]) => {
    return crassData.map((crassDataItem: SearchResponseHitsContent) => {
      return {
        contentType: ContentType.workbook,
        name: crassDataItem.title,
        id: crassDataItem.id.toString(),
        url: new AppUrls({
          siteUrlName: SessionManagementService.instance.sessionSiteUrlName(),
          queryString: window.location.search,
        }).redirectToWorkbookUrl(crassDataItem.id.toString()),
        modifiedTime: crassDataItem.modifiedTime || '',
        crassData: crassDataItem,
        owner: crassDataItem.ownerId
          ? UserMapper.mapOne({
            id: crassDataItem.ownerId?.toString(),
            displayName: crassDataItem.ownerName,
            domainName: crassDataItem.ownerDomain || '',
            username: crassDataItem.ownerAlias || '',
          } as IUser)
          : UserMapper.mapOne(undefined),
        location: crassDataItem.containerName
          ? {
            name: crassDataItem.containerName,
            id: crassDataItem.projectId?.toString(),
            type: crassDataItem.containerType as LocationTypes,
          }
          : undefined,
        sheetCount: crassDataItem.sheetCount ? crassDataItem.sheetCount : undefined,
        size: crassDataItem.size || '',
      };
    }) as Workbook[];
  },
  mapMetadata: (searchResultItems, metadata) => {
    const metaDataWorkbooks: { [workbookId: string]: { thumbnail: string } } = {};
    const favoriteMetaData: { [id: string]: boolean} = {};
    if (metadata.total > 0) {
      metadata.items.workbooks.forEach((item: { id: string; thumbnailUrl: string }) => {
        metaDataWorkbooks[item.id] = { thumbnail: item.thumbnailUrl };
      });
      metadata.items.favorites.forEach((id) => {
        favoriteMetaData[id] = true;
      });
    }

    return searchResultItems.map((searchResultItem) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const crassDataItem = searchResultItem.crassData!;
      return {
        ...searchResultItem,
        thumbnailUrl: metaDataWorkbooks[searchResultItem.id] ? metaDataWorkbooks[searchResultItem.id].thumbnail : '',
        url: new AppUrls({
          siteUrlName: SessionManagementService.instance.sessionSiteUrlName(),
          queryString: window.location.search,
        }).redirectToWorkbookUrl(crassDataItem.id.toString()),
        modifiedTime: crassDataItem.modifiedTime || '',
        description: crassDataItem.description || '',
        tags: crassDataItem.tags || [],
        hitsTotal: crassDataItem.hitsTotal,
        favorite: favoriteMetaData[crassDataItem.id.toString()] ? favoriteMetaData[crassDataItem.id.toString()] : false,
      };
    });
  },
  defaultDisplayMode: 'thumbnail',
  listViewColumns: [
    CommonColumns.name,
    CommonColumns.owner,
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'sheetCount',
        dataKey: 'sheetCount',
        sortable: false,
        headerText: Messages.column_numSheets(),
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: LocalizedCountCell((data: Workbook) => data.sheetCount),
      }
    },
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'size',
        dataKey: 'size',
        sortable: false,
        headerText: Messages.column_size(),
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: SizeCell((data: Workbook) => data.size),
      }
    },
    CommonColumns.location,
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'hitsTotal',
        dataKey: 'hitsTotal',
        sortable: false,
        direction: 'desc',
        headerText: Messages.column_hitsTotal(),
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: LocalizedCountCell((data: Workbook) => data.hitsTotal),
        maxColumnWidth: 10,
      };
    },
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'modified',
        dataKey: 'modifiedTime',
        sortable: false,
        headerText: Messages.column_updated(),
        direction: 'desc',
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: DateTimeCell('modifiedTime'),
        ...{ growToFit: true },
      };
    },
  ],
  cardComponent: WorkbookCard,
  maxItemCountInMixedContentTypeResultPage: 4,
  contentSpaceUrl: (urls: AppUrls, contentId: string) => urls.workbookUrl(contentId),
  quickFilters: [ConnectedOwnerFilter, ConnectedModifiedTimeFilter, ConnectedTagFilter],
  getMetaDataRequests: GetMetaDataRequests.GetWorkbooksRequest,
  getSortOptions: () => [SortBy.getRelevancySortSelection(), SortBy.getPopularitySortSelection()],
  shouldBeAvailable: () => true,
  favoritable: () => true,
};
